import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import type { DispatchOrderRegisterRow } from '@/types/reports';
import { withId } from '@/pages/reports/reportHelpers';
import { isWithinDateRange } from '@/lib/dateRangeFilter';
import PortalDateRangeFilter from './PortalDateRangeFilter';

export default function ClientPortalDispatchOrdersPage() {
  const { t } = useTranslation('clientPortal');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: rawRows = [], isLoading } = useQuery({
    queryKey: ['client-portal', 'dispatch-order-register'],
    queryFn: async () => (await apiClient.get<DispatchOrderRegisterRow[]>('/client-portal/dispatch-order-register')).data,
  });
  const filteredRows = useMemo(
    () => rawRows.filter((r) => isWithinDateRange(r.orderDate, fromDate, toDate)),
    [rawRows, fromDate, toDate],
  );
  const rows = withId(filteredRows, (r) => `${r.dispatchOrderId}-${r.materialCode}`);

  const columns: DataTableColumn<DispatchOrderRegisterRow>[] = [
    { field: 'orderNumber', headerName: t('dispatchOrdersPage.table.orderNumber') },
    { field: 'orderDate', headerName: t('dispatchOrdersPage.table.orderDate'), render: (r) => new Date(r.orderDate).toLocaleDateString() },
    { field: 'customerName', headerName: t('dispatchOrdersPage.table.customerName'), filterable: true },
    { field: 'materialName', headerName: t('dispatchOrdersPage.table.materialName'), filterable: true },
    { field: 'issuedQty', headerName: t('dispatchOrdersPage.table.issuedQty'), align: 'right' },
    { field: 'status', headerName: t('dispatchOrdersPage.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
  ];

  return (
    <>
      <PageHeader title={t('dispatchOrdersPage.title')} subtitle={t('dispatchOrdersPage.subtitle')} />
      <PortalDateRangeFilter fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        searchable
        exportFileName="dispatch-orders"
      />
    </>
  );
}
